// ByAssetClassTab — the "By Asset Class" holdings view: one card per asset-class
// bucket, each showing the class header (swatch, name, desc, allocation, balance,
// weighted YTD, sparkline) above its constituent funds, which are always visible
// (no collapse — matches the design). A managed model spans classes, so inside a
// card its members sit under an "Inside <model>" header, standalone funds after.
// Source of truth: portfolio3.jsx ByAssetClassTab.
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { PerfNum, EmptyState } from '../../../reusable';
import { formatCurrency } from '../../../../utils/format';
import { treeOrFlat, partitionByModel } from '../portfolio.models';
import { shareText } from '../portfolio.adapters';
import { ClassSparkline } from './ClassSparkline';
import { ModelTag } from './ModelRow';
import './by-asset-class.css';

const FundRow = ({ f, child }) => (
  <div className={child ? 'by-class__fund by-class__fund--child' : 'by-class__fund'}>
    <span className="by-class__dot" style={{ background: f.color }} />
    <span className="by-class__fund-nm">{f.name}</span>
    <span className="by-class__fund-tkr">{f.ticker}</span>
    <span className="by-class__fund-bal">{formatCurrency(f.balance)}</span>
    <span className="by-class__fund-pct">{shareText(f)}</span>
    <PerfNum className="by-class__fund-ytd" value={f.ytd} digits={1} />
  </div>
);
FundRow.propTypes = { f: PropTypes.object.isRequired, child: PropTypes.bool };

export function ByAssetClassTab({ assetClasses = [], funds = [], tree, classSpark = {} }) {
  const { t } = useTranslation();
  const byTicker = useMemo(() => Object.fromEntries(funds.map((f) => [f.ticker, f])), [funds]);
  const items = useMemo(() => treeOrFlat(tree, funds), [tree, funds]);

  if (!assetClasses.length) {
    return (
      <EmptyState
        title={t('portfolio.byClass.emptyTitle')}
        message={t('portfolio.byClass.emptyMessage')}
      />
    );
  }

  return (
    <div className="by-class">
      {assetClasses.map((c) => {
        // `tickers` is optional on the wire (AssetClassRollup.tickers), and a class can
        // legitimately hold money with no matching fund row — an unclassified/loan bucket,
        // or a class whose label doesn't appear in holdings. Unguarded, that threw
        // "Cannot read properties of undefined (reading 'map')" and blanked the whole page.
        const tickers = (c.tickers || []).map((t) => byTicker[t]).filter(Boolean);
        const main = tickers[0];
        // Allocation-weighted YTD within the class (share of the class, not the plan).
        const ytd = c.pct ? tickers.reduce((s, f) => s + f.ytd * (f.pct / c.pct), 0) : 0;
        const { groups, standalone } = partitionByModel(tickers, items);

        return (
          <div key={c.key} className="by-class__card">
            <div className="by-class__head">
              <span className="by-class__sw" style={{ background: main?.color }} />
              <div className="by-class__id">
                <span className="by-class__name">{c.label}</span>
                <span className="by-class__desc">{c.desc}</span>
              </div>
              <div className="by-class__stats">
                <span className="by-class__pct">{shareText(c)}</span>
                <span className="by-class__bal">{formatCurrency(c.balance)}</span>
                <PerfNum className="by-class__ytd" value={ytd} digits={1} arrow />
                {/* Wrapped so the stylesheet can drop it on the narrowest phones —
                    Recharts renders its own div with no class of ours. */}
                {classSpark[c.key] && (
                  <span className="by-class__spark">
                    <ClassSparkline data={classSpark[c.key]} color={main?.color} width={68} height={22} />
                  </span>
                )}
              </div>
            </div>

            <div className="by-class__funds">
              {groups.map(({ model, funds: members }) => (
                <div key={model.key} className="by-class__group">
                  <div className="by-class__group-h">
                    <span className="by-class__dot" style={{ background: model.color }} />
                    <span className="by-class__group-nm">{t('portfolio.byClass.insideModel', { name: model.name })}</span>
                    <ModelTag t={t} />
                  </div>
                  {members.map((f) => <FundRow key={f.ticker} f={f} child />)}
                </div>
              ))}
              {standalone.map((f) => <FundRow key={f.ticker} f={f} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

ByAssetClassTab.propTypes = {
  assetClasses: PropTypes.array,
  funds: PropTypes.array,
  /** Model-aware holdings list; names the model a class's funds are held inside. */
  tree: PropTypes.array,
  classSpark: PropTypes.object,
};

export default ByAssetClassTab;
